import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Default sections
import { Model } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useReferenceState } from "@/Common/hooks/ReferenceState";
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Layouts
import AlertWithIcon from "@/Layouts/components/MessagesAlert/AlertWithIcon";
import LargeButton from "@/Layouts/components/Buttons/LargeButton";

// Custom components
import ModelTiered from "../ModelTiered";

// MUI
import { Grid } from "@mui/material";

// Icons
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import moment from "moment";
import { isNil, isFinite } from "lodash";
import { getRelativeYear } from "@/Common/utils/DateUtils";
import { hasOverlap } from "@/Common/utils/ValidationUtils";

// Constants
const MAX_INPUT_VALUE = 1000000;

const isDisabledAdd = ({ isValid, isValidModels, innerModels = [] }) => !(isValid && isValidModels) && Boolean(innerModels?.length);

export default function ModelBased(props) {
	const {
		modelKey = "default",
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more model-based commissions.",
		},
		frontProducts = [],
		backProducts = [],
		makes = [],
		models = [],
		modelsAddons = {
			regularExpression: false,
			miniDeal: false,
			applyRetroactively: false,
		},
		delta = 0.5,
		loading = false,
		loadingMakes = false,
		loadingProducts = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
		addButtonText = "Add Model",
	} = props;

	// Hooks
	const { isValid, validate, errors } = useValidations();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelsUpdate = (updatedModels) => {
		const valid = Boolean(isValid && isValidModels);
		onActionValidation?.(valid);

		if (valid && wasUpdated.current) {
			onActionUpdate?.(updatedModels);
		}
	};

	const [innerModels, setModels] = useCallbackState([], onModelsUpdate);
	const [isValidModels, setIsValidModels, validModelsList, recheckValidModels] = useReferenceState(true, []);

	const handleAddModel = () => {
		const updatedModels = [...innerModels];
		updatedModels.push(Model("", modelsAddons));
		wasUpdated.current = false;
		setModels(updatedModels);
	};

	const handleDeleteModel = (index) => {
		if (index > -1) {
			const updatedModels = [...innerModels];
			updatedModels.splice(index, 1);
			validModelsList.splice(index, 1);
			recheckValidModels();
			saveModels(updatedModels);
		}
	};

	const handleUpdateModel = (key, model) => {
		const updatedModels = [...innerModels];
		updatedModels[key] = model;
		saveModels(updatedModels);
	};

	const saveModels = (updatedModels) => {
		setModels(updatedModels);
		wasUpdated.current = true;
	};

	useMemo(() => {
		if (innerModels?.length) {
			validate([
				{
					definition: {
						useKey: "models",
						operators: {
							conditions: [
								{
									type: "HIERARCHICAL_UNIQUE",
									hierarchy: ["make", "model", "modelYears", "regularExpression"],
								},								
								{
									type: "INTERSECTION",
									on: [
										{
											key: "modelYears.left",
											useModifier: { function: "getRelativeYear", when: { key: "modelYears.rangeType", value: "RELATIVE" } },
										},
										{
											key: "modelYears.right",
											useModifier: { function: "getRelativeYear", when: { key: "modelYears.rangeType", value: "RELATIVE" } },
										},
									],
									useModifier: { function: "hasOverlap" },
									logic: "not",
									applyIf: {
										type: "HIERARCHICAL_UNIQUE",
										hierarchy: ["make", "model"],
										logic: "not",
									},
								},
							],
							modifiers: {
								hasOverlap,
								getRelativeYear,
							},
							maxInvalidConditions: 2,
						},
						errorsMap: ["make", "model", "modelYears", "regularExpression"],
						message: "Invalid model definition",
						validationType: "COMPARATIVE",
					},
					section: innerModels || [],
				},
			]);
		}
	}, [innerModels, validate]);

	useEffect(() => {
		return () => {
			setIsValidModels(true);
		};
	}, [setIsValidModels]);

	useEffect(() => {
		setModels(clone(models));

		return () => {
			setModels([]);
			wasUpdated.current = false;
		};
	}, [models, setModels]);

	return (
		<Grid container>
			<Grid item xs={12} className={"models-tiered-area"}>
				{innerModels.map((model, index) => {
					return (
						<ModelTiered
							index={index}
							key={`model_tiered_${modelKey}_${index}`}
							modelKey={modelKey}
							delta={delta || 0.5}
							frontProducts={frontProducts}
							backProducts={backProducts}
							makes={makes}
							model={model}
							modelsAddons={modelsAddons}
							loading={loading}
							loadingProducts={loadingProducts}
							loadingMakes={loadingMakes}
							readOnly={readOnly}
							useRegexModels={true}
							errors={errors}
							onActionDelete={() => handleDeleteModel(index)}
							onActionUpdate={(updatedModel) => handleUpdateModel(index, updatedModel)}
							onActionValidation={(value) => {
								validModelsList[index] = value;
								recheckValidModels();
							}}
						/>
					);
				})}

				{Boolean(emptyError.error && !innerModels?.length) && (
					<Grid container justifyContent={"center"} mb={2}>
						<AlertWithIcon
							className={"error-message-span"}
							text={emptyError.emptyMessage || "Error."}
							icon={<ErrorOutlineOutlinedIcon size="small" />}
							textStyle={{ marginLeft: "3px" }}
						/>
					</Grid>
				)}
			</Grid>
			<Grid className={"large-button-container"} item xs={12}>
				<LargeButton
					disabled={isDisabledAdd({ isValid, isValidModels, innerModels }) || readOnly}
					className={"btn-primary"}
					text={addButtonText}
					onClick={handleAddModel}
					endIcon={<AddCircleRoundedIcon />}
				/>
			</Grid>
		</Grid>
	);
}
ModelBased.propTypes = {
	modelKey: PropTypes.string,
	emptyError: PropTypes.shape({
		error: PropTypes.bool,
		emptyMessage: PropTypes.string,
	}),
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	makes: PropTypes.array,
	models: PropTypes.array,
	delta: PropTypes.number,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	modelsAddons: PropTypes.shape({
		regularExpression: PropTypes.bool,
		miniDeal: PropTypes.bool,
		applyRetroactively: PropTypes.bool,
	}),
	addButtonText: PropTypes.string,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
